import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Logo, LogoMimeType } from '@shared/components/logo-preview/logo-preview.component';
import {
  ImageDimensions,
  LogoParameterSettings,
} from '@shared/components/logo-select/logo-select.model';
import { NotificationLevel } from '@shared/services/notification/notification-level.model';
import { CustomSanitizerService } from '@shared/services/util/custom-sanitizer.service';
import { IconSize, IconWeight } from '@widgets/eop-icon/eop-icon.component';
import { SnackbarService } from '@shared/services/notification/snackbar.service';

@Component({
  selector: 'eop-logo-select',
  templateUrl: './logo-select.component.html',
  styleUrls: ['./logo-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LogoSelectComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class LogoSelectComponent implements ControlValueAccessor, OnChanges {
  private readonly sanitizer = inject(CustomSanitizerService);
  private readonly translate = inject(TranslateService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild('fileInput') fileInputRef: ElementRef;

  @Input() dataTestId: string;
  @Input() customMineTypes: LogoMimeType[] = [];
  @Input({
    required: true,
  })
  uploadInfoText: string;
  @Input()
  pngLogoSettings: LogoParameterSettings;
  @Input()
  svgLogoSettings: LogoParameterSettings;
  SVGMimeType = LogoMimeType.SVG;

  readonly IconWeight = IconWeight;
  readonly IconSize = IconSize;

  logo: Logo;
  disabled: boolean;
  loading: boolean;

  convertedSvgToPngBase64: string;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customMineTypes'] && Array.isArray(this.customMineTypes)) {
      if (this.customMineTypes.length > 0) {
        this.SVGMimeType = undefined;
      }
    }
  }

  registerOnChange(fn: (value: Logo) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(logo: Logo): void {
    this.logo = logo;
    this.loading = !logo;
  }

  onSelectFile(file: File) {
    if (file) {
      const logo: Logo = {
        data: '',
        defaultLogo: false,
        mime: undefined,
        fileName: '',
      };

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async (eventReader: ProgressEvent) => {
        if (this.customMineTypes && this.customMineTypes.length > 0) {
          if (!this.customMineTypes.includes(file.type as LogoMimeType)) {
            return;
          }
        } else if (file.type !== this.SVGMimeType) {
          return;
        }

        const fileResult = (eventReader.target as FileReader).result as string;

        if (await this.invalidFileSize(file, fileResult, file.type as LogoMimeType)) {
          this.snackbarService.openSnackbar(
            this.translate.instant('GLOBAL.MAX_FILE_SIZE_IS') +
              ` ${file.type === LogoMimeType.PNG ? this.pngLogoSettings.maxSizeKB / 1024 : this.svgLogoSettings.maxSizeKB / 1024} MB`,
            NotificationLevel.ERROR,
            8000
          );
        } else {
          if (file.type === LogoMimeType.PNG) {
            this.wrapPngToSvgFileAndSetLogo(logo, file);
          }
          if (file.type === LogoMimeType.SVG) {
            const imageSplit = fileResult.split(',');
            logo.data = this.sanitizeBase64(imageSplit[1]);
            logo.mime = imageSplit[0].split(';')[0].split(':')[1] as LogoMimeType;
            logo.defaultLogo = false;
            logo.fileName = file.name;
            this.setLogo(logo);

            this.convertBase64SvgToPng(fileResult).then(pngBase64 => {
              this.convertedSvgToPngBase64 = pngBase64;
              logo.originalFile = {
                data: pngBase64.split(',')[1],
                mime: LogoMimeType.PNG,
                defaultLogo: false,
                fileName: file.name,
              };
            });
          }
        }
      };
    }
  }

  convertBase64SvgToPng(svgBase64: string): Promise<string> {
    return new Promise(async (resolve, reject) => {
      try {
        // Decode the Base64 SVG string
        const base64ContentArray = svgBase64.split(',');
        const svgContent = atob(this.sanitizeBase64(base64ContentArray[1])); // Decoded SVG content
        const mimeType = base64ContentArray[0]?.split(':')[1]?.split(';')[0] || 'image/svg+xml';

        // Create a Blob from the SVG content
        const svgBlob = new Blob([svgContent], { type: mimeType });
        const url = URL.createObjectURL(svgBlob);

        // Get the SVG dimensions
        const svgDimensions = await this.getImageDimensionsFromSvgBase64(svgBase64);

        // Calculate dimensions based on max dimension of 256px while preserving aspect ratio
        const maxDimension = 256;
        let pngWidth: number, pngHeight: number;

        // necessary for chrome when svg image doesn't have width and height dimensions to convert it with correct aspect ratio
        // necessary for firefox and safari to doesn't have width and height dimensions to don't use image 0px * 0px
        if (!svgDimensions.width || !svgDimensions.height) {
          svgDimensions.width = maxDimension;
          svgDimensions.height = maxDimension;
        }

        if (svgDimensions.width >= svgDimensions.height) {
          // Width > Height case
          const aspectRatio = svgDimensions.height / svgDimensions.width;
          pngWidth = maxDimension;
          pngHeight = Math.round(maxDimension * aspectRatio);
        } else {
          // Height > Width case
          const aspectRatio = svgDimensions.width / svgDimensions.height;
          pngHeight = maxDimension;
          pngWidth = Math.round(maxDimension * aspectRatio);
        }

        // Create an Image element
        const img = new Image();
        img.width = pngWidth;
        img.height = pngHeight;

        // Set up image load handler
        img.onload = () => {
          // Create a canvas element with the calculated dimensions
          const canvas = document.createElement('canvas');
          canvas.width = pngWidth;
          canvas.height = pngHeight;

          // Get the canvas context
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          if (!ctx) {
            reject('Failed to get canvas context');
            return;
          }

          // Clear canvas with transparent background
          ctx.fillStyle = 'transparent';
          ctx.fillRect(0, 0, pngWidth, pngHeight);

          // Draw the SVG image onto the canvas
          ctx.drawImage(img, 0, 0, pngWidth, pngHeight);

          // Reduce color depth to decrease entropy
          this.reduceColorDepth(ctx, pngWidth, pngHeight);

          // Convert the canvas to a PNG with lower quality to reduce entropy
          // Safari tends to generate higher entropy PNGs
          const pngBase64 = canvas.toDataURL('image/png', 0.1);

          // Cleanup
          URL.revokeObjectURL(url);

          resolve(pngBase64);
        };

        // Set up error handler
        img.onerror = err => {
          URL.revokeObjectURL(url);
          reject(`Failed to load SVG for conversion: ${err}`);
        };

        // Set the image source to the SVG blob URL
        img.src = url;
      } catch (error) {
        reject(`Conversion failed: ${error}`);
      }
    });
  }

  removeLogo(): void {
    this.fileInputRef.nativeElement.value = null;
    this.setLogo(null);
  }

  private onChangeCallback: (value: Logo) => void = () => {};

  private onTouchedCallback: () => void = () => {};

  private wrapPngToSvgFileAndSetLogo(logo: Logo, file: File) {
    const reader = new FileReader();
    const base64Promise = new Promise<string>((resolve, reject) => {
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

    base64Promise
      .then(base64Data => {
        const svgContent = `<?xml version="1.0" encoding="UTF-8"?>
    <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="100%" height="100%" viewBox="0 0 100 100">
      <image xlink:href="${base64Data}" width="100%" height="100%" />
    </svg>`;

        const svgBase64 = btoa(svgContent);

        // Set the encoded SVG as logo data
        logo.originalFile = {
          data: base64Data.split(',')[1],
          mime: LogoMimeType.PNG,
          defaultLogo: false,
          fileName: file.name,
        };
        logo.data = this.sanitizeBase64(svgBase64); // Convert SVG to Base64
        logo.mime = LogoMimeType.SVG;
        logo.defaultLogo = false;
        logo.fileName = file.name;
        this.setLogo(logo);
      })
      .catch(() => {
        this.snackbarService.openSnackbar(
          this.translate.instant('TERMINAL_MANAGEMENT.LOGO_CONTAINS_MALICIOUS_CONTENT'),
          NotificationLevel.ERROR,
          8000
        );
      });
  }

  private sanitizeBase64(base64: string) {
    const readable = atob(base64);
    const sanitizationResult = this.sanitizer.sanitizeSvgString(readable);
    const cleanResult = sanitizationResult.clean;
    return btoa(cleanResult);
  }

  private getImageDimensionsFromSvgBase64(svgBase64: string): Promise<ImageDimensions> {
    return new Promise((resolve, reject) => {
      const image = new Image();

      // Handle the "load" and "error" events
      image.onload = () => {
        resolve({
          width: image.width,
          height: image.height,
        });
      };

      image.onerror = () => {
        reject(new Error('Failed to load SVG from Base64.'));
      };

      // Set the `src` of the image to the Base64 SVG
      image.src = svgBase64;
    });
  }

  private setLogo(logo: Logo) {
    this.writeValue(logo);
    this.onChangeCallback(logo);
    this.onTouchedCallback();
    this.cdr.markForCheck();
  }

  /**
   * Reduces the color depth of an image on a canvas to decrease entropy
   * @param ctx The canvas 2D context
   * @param width The width of the canvas
   * @param height The height of the canvas
   */
  private reduceColorDepth(ctx: CanvasRenderingContext2D, width: number, height: number): void {
    // Get the image data from the canvas
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    // Reduce color depth by quantizing each color channel
    // Using 3 bits for red and green, 2 bits for blue (8 x 8 x 4 = 256 colors)
    const redBits = 3;
    const greenBits = 3;
    const blueBits = 2;

    for (let i = 0; i < data.length; i += 4) {
      // Skip fully transparent pixels
      if (data[i + 3] === 0) continue;

      // Quantize each color channel
      data[i] = this.quantizeColorChannel(data[i], redBits); // Red
      data[i + 1] = this.quantizeColorChannel(data[i + 1], greenBits); // Green
      data[i + 2] = this.quantizeColorChannel(data[i + 2], blueBits); // Blue

      // Alpha channel remains unchanged
    }

    // Put the modified image data back on the canvas
    ctx.putImageData(imageData, 0, 0);
  }

  /**
   * Quantizes a color channel value to the specified number of bits
   * @param value The original color channel value (0-255)
   * @param bits The number of bits to quantize to
   * @returns The quantized color channel value
   */
  private quantizeColorChannel(value: number, bits: number): number {
    // Calculate the number of levels based on bits
    const levels = Math.pow(2, bits);
    // Calculate the step size
    const step = 256 / levels;

    // Special case for maximum values to ensure they remain at 255
    // This is particularly important for white (255, 255, 255)
    if (value >= 255 - step / 2) {
      return 255;
    }

    // Quantize the value to the nearest level instead of flooring
    // This ensures colors like white (255) remain white after quantization
    return Math.round(value / step) * step;
  }

  private async invalidFileSize(file: File, fileResult: string, mime: LogoMimeType) {
    const parameterSettings: LogoParameterSettings =
      mime === LogoMimeType.SVG ? this.svgLogoSettings : this.pngLogoSettings;

    if (parameterSettings) {
      if (parameterSettings.maxSizeKB && parameterSettings.maxSizeKB * 1024 < file.size) {
        return true;
      }

      if (parameterSettings.minSizePixel) {
        const imageDimensions = await this.getImageDimensions(fileResult);

        if (parameterSettings.minSizePixel > imageDimensions.height * imageDimensions.width) {
          return true;
        }
      }
    }

    return false;
  }

  private getImageDimensions(fileResult: string): Promise<ImageDimensions> {
    return new Promise<ImageDimensions>(resolve => {
      const img = new Image();
      img.onload = () => {
        resolve({
          height: img.height,
          width: img.width,
        });
      };
      img.src = fileResult;
    });
  }
}
